import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { fetchAllTests } from "../../../services/mongod.data";

// Async Thunk — Fetch Tests
export const fetchTests = createAsyncThunk(
  "abtest/fetchTests",
  async ({ cf_username, forceRefresh=false }, { getState, rejectWithValue }) => {
    const { tests, lastFetched } = getState().abtest;

    console.log("ABTest: Starting fetchTests thunk...", { cf_username, forceRefresh });
    // Cache check
    if (tests.length > 0 && lastFetched && !forceRefresh) {
      console.log("Redux: Using cached tests");
      return { fromCache: true, tests };
    }

    if (!cf_username) {
      return rejectWithValue("CF username is required");
    }

    try {
      const data = await fetchAllTests(cf_username)
      if (!data.success) {
        throw new Error(data.error || "Failed to fetch tests");
      }

      return { fromCache: false, tests: data.tests };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

//Helper: filter out valid tests
const filterValidTests = (tests) =>
  tests.filter((t) => t.basicInfo && t._id !== "store_info");

//Initial State
const initialState = {
  tests: [],
  selectedTest: null,
  loading: false,
  error: null,
  lastFetched: null,
};

// Slice
const abtestSlice = createSlice({
  name: "abtest",
  initialState,
  reducers: {
    setSelectedTest: (state, action) => {
      const testId = action.payload;
      state.selectedTest =
        state.tests.find(
          (t) => t._id === testId || t.metadata?.testId === testId
        ) || null;
    },

    addTest: (state, action) => {
      const test = action.payload;
      if (test.basicInfo && test._id !== "store_info") {
        state.tests.push(test);
      }
    },

    updateTestStatus: (state, action) => {
      const { testId, status } = action.payload;

      state.tests = state.tests.map((test) =>
        test._id === testId || test.metadata?.testId === testId
          ? {
              ...test,
              metadata: {
                ...test.metadata,
                status,
                updatedAt: new Date().toISOString(),
              },
            }
          : test
      );
    },

    updateTest: (state, action) => {
      const { testId, updates } = action.payload;

      state.tests = state.tests.map((test) =>
        test._id === testId || test.metadata?.testId === testId
          ? { ...test, ...updates }
          : test
      );
    },

    removeTest: (state, action) => {
      const testId = action.payload;
      state.tests = state.tests.filter(
        (t) => t._id !== testId && t.metadata?.testId !== testId
      );
    },

    clearTests: (state) => {
      state.tests = [];
      state.selectedTest = null;
      state.lastFetched = null;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTests.fulfilled, (state, action) => {
        state.loading = false;

        if (!action.payload.fromCache) {
          state.tests = filterValidTests(action.payload.tests);
          state.lastFetched = new Date().toISOString();
        }
      })
      .addCase(fetchTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch tests";
      });
  },
});

// Export actions
export const {
  setSelectedTest,
  addTest,
  updateTestStatus,
  updateTest,
  removeTest,
  clearTests,
} = abtestSlice.actions;

export const selectTests = (state) => state.abtest.tests;
export const selectLoading = (state) => state.abtest.loading;
export const selectError = (state) => state.abtest.error;
export const selectSelectedTest = (state) => state.abtest.selectedTest;

export const selectTestsByStatus = (status) =>
  createSelector([selectTests], (tests) =>
    status === "all"
      ? tests
      : tests.filter((t) => t.metadata?.status === status)
  );

export const selectStatusCounts = createSelector([selectTests], (tests) => ({
  all: tests.length,
  active: tests.filter((t) => t.metadata?.status === "active").length,
  paused: tests.filter((t) => t.metadata?.status === "paused").length,
  stopped: tests.filter((t) => t.metadata?.status === "stopped").length,
  completed: tests.filter((t) => t.metadata?.status === "completed").length,
  draft: tests.filter((t) => t.metadata?.status === "draft").length,
}));

export const selectTestById = (testId) =>
  createSelector([selectTests], (tests) =>
    tests.find((t) => t._id === testId || t.metadata?.testId === testId)
  );

// Reducer
export default abtestSlice.reducer;